import { CalculationOptions, ScoreResultIf } from '../index';
import { ScoreIf } from '../score-type/score-if';
import { ValidScore } from '../score-type/valid-score';

export function calculateTotalPointsScore(scores?: Array<ScoreIf>, options?: CalculationOptions): ScoreResultIf | undefined {
  let debug: boolean = (options && options.debug === true) || false;
  if (scores == null || scores.length === 0) {
    if (debug) { console.log('No scores were passed in to calculate.'); }
    return undefined;
  }

  let filteredScores: Array<ScoreIf> = scores.filter((score: ScoreIf): boolean => {
    return score.getPoints() != null;
  });

  let validScoreCount = filteredScores.length;
  if (validScoreCount === 0) {
    if (debug) { console.log('No scores with points were passed in to calculate.'); }
    return undefined;
  }

  let nonInfinityScoreCount = validScoreCount;
  let validFilteredScores: Array<ValidScore> = filteredScores.map<ValidScore>((score: ScoreIf): ValidScore => {
    let validScore: ValidScore = new ValidScore(score);
    if (validScore.isPercentInfinity()) {
      if (debug) { console.log('Found score that is inifinity'); }
      nonInfinityScoreCount--;
    }
    return validScore;
  });

  let dropLowScores = 0,
    dropHighScores = 0;
  if (options != null && options.dropScores != null) {
    dropLowScores = (options.dropScores.dropLowScores != null && options.dropScores.dropLowScores > 0) ? options.dropScores.dropLowScores : 0;
    dropHighScores = (options.dropScores.dropHighScores != null && options.dropScores.dropHighScores > 0) ? options.dropScores.dropHighScores : 0;
  }
  if ((dropLowScores > 0 || dropHighScores > 0) && nonInfinityScoreCount <= (dropLowScores + dropHighScores)) {
    if (debug) { console.log('Ignoring options.dropScores calculation settings as there are not enough scores to allow dropping'); }
    dropLowScores = 0;
    dropHighScores = 0;
  }

  let droppedScores: Array<ScoreIf> | undefined;
  if (dropLowScores > 0 || dropHighScores > 0) {
    let studentsFavor: boolean = options != null && options.dropScores != null && options.dropScores.studentsFavor === true;
    let filtered: { droppedScores: Array<ValidScore>, scores: Array<ValidScore> } = doDropScores(debug, validFilteredScores, dropLowScores, dropHighScores, studentsFavor);
    validFilteredScores = filtered.scores;
    droppedScores = filtered.droppedScores.map((score: ValidScore): ScoreIf => {
      return score.getSourceScore();
    });
    if (debug) { console.log(`Dropped ${droppedScores.length} score(s): ${JSON.stringify(droppedScores)}`); }
  }

  if (debug) { console.log(`Using ${validFilteredScores.length} score(s) to calcuate metrics`); }

  let scoreResult: ScoreResultIf = getCalculationMetrics(debug, validFilteredScores);
  scoreResult.droppedScores = droppedScores;
  return scoreResult;
}

function getCalculationMetrics(debug: boolean, scores: Array<ValidScore>): ScoreResultIf {
  let points = 0,
    totalPoints = 0,
    weightedPoints = 0,
    totalWeightedPoints = 0;

  scores.forEach((score: ValidScore): void => {
    if (debug) { console.log(`Running total: points: ${points}, totalPoints: ${totalPoints}, weightedPoints: ${weightedPoints}, totalWeightedPoints: ${totalWeightedPoints}, adding score: ${JSON.stringify(score)}`); }
    points += score.getPoints();
    totalPoints += score.getTotalPoints();
    weightedPoints += score.getPoints() * score.getWeight();
    totalWeightedPoints += score.getTotalPoints() * score.getWeight();
  });
  if (debug) { console.log(`Total: points: ${points}, totalPoints: ${totalPoints}, weightedPoints: ${weightedPoints}, totalWeightedPoints: ${totalWeightedPoints}`); }

  return {
    points: points,
    totalPoints: totalPoints,
    weightedPoints: weightedPoints,
    totalWeightedPoints: totalWeightedPoints
  };
}

function doDropScores(debug: boolean, validFilteredScores: Array<ValidScore>, dropLowScores: number, dropHighScores: number, studentsFavor: boolean): { droppedScores: Array<ValidScore>, scores: Array<ValidScore> } {
  if (studentsFavor === true) {
    validFilteredScores.sort(sortScoresByAmountOfWeightedPointsMissing);
  } else {
    validFilteredScores.sort(sortScoresByPercentAscThenWeightedPointsDesc);
  }

  if (debug) { console.log(`Sorted score(s) before drop: ${JSON.stringify(validFilteredScores)}`); }

  let droppedScores: Array<ValidScore> = [];
  if (dropLowScores > 0) {
    droppedScores.push(...validFilteredScores.slice(0, dropLowScores));
  }
  if (dropHighScores > 0) {
    droppedScores.push(...validFilteredScores.slice(validFilteredScores.length - dropHighScores, validFilteredScores.length));
  }

  return {
    droppedScores: droppedScores,
    scores: validFilteredScores.splice(dropLowScores, validFilteredScores.length - dropHighScores - 1)
  };
}

function sortScoresByPercentAscThenWeightedPointsDesc(s1: ValidScore, s2: ValidScore): number {
  //Percent asc
  let percent1 = s1.getPercent();
  let percent2 = s2.getPercent();
  if (percent1 < percent2) {
    return -1;
  } else if (percent1 > percent2) {
    return 1;
  }

  //Weighted Points desc
  let weightedPointsPossible1 = s1.getWeightedPointsPossible();
  let weightedPointsPossible2 = s2.getWeightedPointsPossible();
  if (weightedPointsPossible2 < weightedPointsPossible1) {
    return -1;
  } else if (weightedPointsPossible2 > weightedPointsPossible1) {
    return 1;
  } else {
    return 0;
  }
}

function sortScoresByAmountOfWeightedPointsMissing(s1: ValidScore, s2: ValidScore): number {
  //How many points are they missing
  let missingPoints1 = s1.getWeightedMissingPoints();
  let missingPoints2 = s2.getWeightedMissingPoints();

  if (missingPoints1 > missingPoints2) {
    return -1;
  } else if (missingPoints1 < missingPoints2) {
    return 1;
  } else {
    return 0;
  }
}
